import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/auth.store";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import {
  getActiveHelpGuide,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { isZeroPilotPathAllowed } from "../auth/pilot";

interface AuthRouteProps {
  children: React.ReactNode;
  /** Full permission string (when not org-scoped). */
  requiredPermission?: string;
  /** Build permission from the active organization id. */
  orgScopedPermission?: (orgId: string) => string;
  legacyRoles?: readonly string[];
  /** Optional extra gate (e.g. platform approval permissions). */
  accessCheck?: () => boolean;
}

/**
 * Authenticated route with optional permission gate (dual-mode legacy fallback).
 * Any active Help `helpGuide` skips permission gates so tips work for every
 * signed-in user (login is the only Help constraint).
 */
const AuthRoute: React.FC<AuthRouteProps> = ({
  children,
  requiredPermission,
  orgScopedPermission,
  legacyRoles,
  accessCheck,
}) => {
  const { isAuthenticated, tokenPayload } = useAuthStore();
  const { can, orgId, isPlatformAdmin } = useAuthPermissions();
  const location = useLocation();
  const helpGuideParam = new URLSearchParams(location.search).get("helpGuide");
  if (helpGuideParam) {
    markHelpGuideActive(helpGuideParam);
  }
  const activeHelpGuide = helpGuideParam || getActiveHelpGuide();
  // Help "Go to" tips: no permission gates — any signed-in user can follow them.
  const helpGuideBypass = Boolean(activeHelpGuide);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Layout renders the static Pilot Mode lock without mounting the page.
  if (tokenPayload?.is_pilot && !isZeroPilotPathAllowed(location.pathname)) {
    return <>{children}</>;
  }

  const resolvedPermission =
    orgScopedPermission && orgId
      ? orgScopedPermission(orgId)
      : requiredPermission;

  if (
    resolvedPermission &&
    !helpGuideBypass &&
    !isPlatformAdmin &&
    !can(resolvedPermission, legacyRoles) &&
    !(accessCheck?.() ?? false)
  ) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AuthRoute;
